import type { AdaptedNavGroup, AdaptedNavItem } from '@command-center/ui-system';
import {
  hasClientPortalCapability,
  type ClientPortalCapabilities,
  type ClientPortalFeatureKey,
} from '@oneup/shared';
import { resolveIcon } from '../icons';
import type { ClientModuleConfig } from '../client-module';
import { agentsModule } from './agents.module';
import { callsModule } from './calls.module';
import {
  websitesModule,
  workModule,
  invoicesModule,
  subscriptionsModule,
  formsModule,
  docsModule,
} from './roadmap.modules';
import { aliceActivityModule, organizationsModule, switchboardModule } from './workspace.modules';

/**
 * The Client Portal module registry. Order here is the main navigation order.
 *
 * Overview (the organization workspace) is the portal home and the first
 * entry. The former standalone "Homebase" nav entry has been retired; its `/`
 * deep link now redirects here.
 */
export const CLIENT_MODULES: ClientModuleConfig[] = [
  organizationsModule,
  websitesModule,
  workModule,
  formsModule,
  switchboardModule,
  agentsModule,
  callsModule,
  aliceActivityModule,
  docsModule,
  invoicesModule,
  subscriptionsModule,
];

/**
 * Billing lives in the workspace menu, Calls lives behind the AI Voice Agents
 * tabs, and AI Activity lives in Settings. The visible modules use one quiet,
 * unlabelled list, matching the One Up Admin shell instead of teaching clients
 * internal product-section names.
 */
const MAIN_NAV_HIDDEN_MODULES = new Set([
  'invoices',
  'subscriptions',
  'calls',
  'alice-activity',
]);

const MODULE_FEATURES: Partial<Record<string, ClientPortalFeatureKey>> = {
  websites: 'websites',
  work: 'tasks',
  forms: 'forms',
  switchboard: 'automations',
  agents: 'voice_agents',
  calls: 'calls',
  'alice-activity': 'alice_chat',
  docs: 'docs',
  invoices: 'billing',
  subscriptions: 'billing',
};

export function getClientModules(capabilities?: ClientPortalCapabilities): ClientModuleConfig[] {
  if (!capabilities) return CLIENT_MODULES;
  return CLIENT_MODULES.filter((module) => {
    const feature = MODULE_FEATURES[module.id];
    return !feature || hasClientPortalCapability(capabilities, feature);
  });
}

export function getClientModuleByRoute(route: string): ClientModuleConfig | undefined {
  return CLIENT_MODULES.find((module) => module.route === route);
}

export function getClientModuleById(id: string): ClientModuleConfig | undefined {
  return CLIENT_MODULES.find((module) => module.id === id);
}

/** Builds grouped, ordered nav for the shared ui-system Sidebar. */
export function getClientNavigationGroups(capabilities?: ClientPortalCapabilities): AdaptedNavGroup[] {
  const items: AdaptedNavItem[] = getClientModules(capabilities).flatMap((module) => {
    if (MAIN_NAV_HIDDEN_MODULES.has(module.id)) return [];
    return [{
      id: module.id,
      to: module.navRoute ?? module.route,
      label: module.label,
      icon: resolveIcon(module.icon),
      moduleId: module.id,
      placeholder: module.status === 'coming-soon',
    }];
  });

  return [{ label: '', items, collapsible: false }];
}
